import isEqual from 'lodash/isEqual';
import * as Yup from 'yup';

import { decimalIn } from 'Helpers/currencyFormat';
import { formatDateLong } from 'Helpers/dateFormat';

export const REPORT_KEY = 'ManagementPack';

export const SNAPSHOT_COMPLETED = 'completed';
export const SNAPSHOT_FAILED = 'failed';

const DEFAULT_SETTINGS = {
    period: null,
    as_at: null,
    comparison_budget_id: null,
    movement_basis_pack_id: null,
    confidentiality_label: 'Confidential',
    commentary_enabled: true,
    draft_commentary: false,
    budget_unit: 'whole',
};

const DEFAULT_OPTIONS = {
    prepared_by: '',
    watermark_drafts: true,
};

/**
 * A chart value-axis tick as the .pptx prints it: grouped, to `decimals`
 * places, a negative in brackets and zero as "0".
 */
export const formatPackAxis = (value, decimals = 0) => {
    const factor = 10 ** decimals;
    const rounded = Math.round(Number(value) * factor) / factor;

    if (rounded === 0 || Number.isNaN(rounded)) {
        return '0';
    }

    const formatted = decimalIn('en-US', Math.abs(rounded), decimals);

    return rounded < 0 ? `(${formatted})` : formatted;
};

/**
 * Fill the ID and PAGE_KEY path segments of a builder URL template.
 */
export const fillUrl = (template, { id, pageKey, subjectType, subjectKey } = {}) => {
    let url = template || '';

    if (id !== undefined) {
        url = url.replace(/\/ID(?=\/|\?|$)/, `/${id}`);
    }

    if (pageKey !== undefined) {
        url = url.replace(/\/PAGE_KEY(?=\/|\?|$)/, `/${encodeURIComponent(pageKey)}`);
    }

    // The status override URL ends in placeholder `/area/subject_key` segments.
    if (subjectType !== undefined && subjectKey !== undefined) {
        url = url.replace(/\/area\/subject_key(?=\?|$)/, `/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectKey)}`);
    }

    return url;
};

const sortedIds = (ids) => [...(ids || [])].map(Number).sort((a, b) => a - b);

/**
 * The selection's scope as the server signs it: `org`, or the sorted cost
 * centres, followed by their departments when the user's departments are part
 * of the scope (`cc:1,2|d:7`).
 */
export const scopeSignatureFor = (scope, options = {}) => {
    if (scope.org) {
        return 'org';
    }

    const costcentreIds = sortedIds(scope.costcentreIds);
    let signature = `cc:${costcentreIds.join(',')}`;

    if (options.scopeDepartmentsInKey) {
        const allowed = options.allowedDepartmentIds ? new Set(options.allowedDepartmentIds.map(Number)) : null;
        const departments = new Set(
            (options.costcentres || [])
                .filter((costcentre) => costcentreIds.includes(Number(costcentre.value)) && costcentre.departmentId != null)
                .map((costcentre) => Number(costcentre.departmentId))
                .filter((id) => !allowed || allowed.has(id)),
        );

        signature += `|d:${sortedIds([...departments]).join(',')}`;
    }

    return signature;
};

export const sameScope = (pack, scope, options) => pack.scopeSignature === scopeSignatureFor(scope, options);

const sameCostcentres = (pack, scope) => {
    if (scope.org || pack.isOrgScope) {
        return !!scope.org && !!pack.isOrgScope;
    }

    return isEqual(sortedIds(pack.costcentreIds), sortedIds(scope.costcentreIds));
};

/**
 * The pack for the selected period and scope. A pack chosen by id wins when it
 * covers the selected period and cost centres, even when its scope key differs
 * from the one this user would build (another user's department-narrowed pack).
 * Otherwise the highest revision of the user's own scope.
 */
export const findPack = (packs, values, options, selectedPackId = null) => {
    const period = values.settings.period;
    const selected = packs.find((pack) => pack.id === selectedPackId && pack.period === period && sameCostcentres(pack, values.scope));

    if (selected) {
        return selected;
    }

    const signature = scopeSignatureFor(values.scope, options);

    return packs.filter((pack) => pack.period === period && pack.scopeSignature === signature).sort((a, b) => b.revision - a.revision)[0] || null;
};

/**
 * The label a pack has as a movement basis option.
 */
export const basisLabel = (pack) => {
    let label = $t('{{period}} (issued {{date}})', { period: pack.periodLabel, date: formatDateLong(pack.issuedAt) });

    if (pack.revision > 1) {
        label = $t('{{label}}, revision {{revision}}', { label, revision: pack.revision });
    }

    return pack.isSuperseded ? $t('{{label}} · superseded', { label }) : label;
};

/**
 * Issued packs with the same scope from earlier periods, and the issued
 * revisions of the selected period, newest first, flagging the default: the
 * newest earlier-period pack no later revision has replaced. Used when there
 * is no pack yet to read `movementBasisOptions` from.
 */
export const movementBasisOptionsFor = (packs, values, options) => {
    const period = values.settings.period;
    const issued = packs
        .filter((pack) => pack.isIssued && sameScope(pack, values.scope, options) && pack.period <= period)
        .sort((a, b) => b.period.localeCompare(a.period) || b.revision - a.revision);
    const defaultId = issued.find((pack) => pack.period < period && !pack.isSuperseded)?.id ?? null;

    return issued.map((pack) => ({
        id: pack.id,
        label: basisLabel(pack),
        isDefault: pack.id === defaultId,
        isSuperseded: !!pack.isSuperseded,
        revision: pack.revision,
    }));
};

/**
 * Whether the pack is the issued revision a reissue can start from: issued,
 * not superseded, and with no later revision (issued or draft) of its period
 * and scope.
 */
export const isLatestIssued = (pack, packs) =>
    !!pack?.isIssued &&
    !pack.isSuperseded &&
    !packs.some(
        (other) => other.id !== pack.id && other.period === pack.period && other.scopeSignature === pack.scopeSignature && other.revision > pack.revision,
    );

/**
 * Section keys no longer in use, and how each stored option maps onto the
 * sections that replace it. Mirrors PackConfigurationData::LEGACY_SECTIONS.
 */
const LEGACY_SECTIONS = {
    issues_decisions: {
        issues: { issue_min_rating: 'min_rating', show_overdue_actions: 'show_overdue_actions' },
        decisions: { decision_min_rating: 'min_rating', show_overdue_actions: 'show_overdue_actions' },
    },
};

/**
 * Stored sections with each legacy entry read as the sections that replace it,
 * in its place and with its enabled flag. A replacement stored under its own
 * key wins over the legacy entry.
 */
export const splitLegacySections = (sections = []) => {
    const stored = new Set(sections.map((section) => section.key));

    return sections.flatMap((section) => {
        const replacements = LEGACY_SECTIONS[section.key];

        if (!replacements) {
            return [section];
        }

        const options = section.options || {};

        return Object.entries(replacements)
            .filter(([key]) => !stored.has(key))
            .map(([key, optionMap]) => ({
                key,
                enabled: section.enabled ?? true,
                options: Object.fromEntries(
                    Object.entries(optionMap)
                        .filter(([from]) => from in options)
                        .map(([from, to]) => [to, options[from]]),
                ),
            }));
    });
};

/**
 * A plans section saved before cost centre plans were a group of their own,
 * with them set as its departmental choice, which used to include them.
 * Mirrors PlansSectionOptions::fromArray().
 */
const withCostcentrePlans = (section) => {
    const groups = section.options?.plan_groups;

    if (section.key !== 'plans' || !groups || typeof groups !== 'object' || 'costcentre' in groups || typeof groups.departmental !== 'boolean') {
        return section;
    }

    return { ...section, options: { ...section.options, plan_groups: { ...groups, costcentre: groups.departmental } } };
};

/**
 * A stored configuration (snake_case, as the backend serialises it) merged over
 * the defaults, so a partial or older saved set still fills every field.
 */
export const normaliseConfiguration = (configuration = {}) => ({
    settings: { ...DEFAULT_SETTINGS, ...(configuration.settings || {}) },
    sections: splitLegacySections(configuration.sections || []).map((section) => withCostcentrePlans({ enabled: true, options: {}, ...section })),
    options: { ...DEFAULT_OPTIONS, ...(configuration.options || {}), prepared_by: configuration.options?.prepared_by || '' },
});

/**
 * The configuration part of the form values, in the shape every endpoint takes.
 */
export const configurationOf = (values) => ({
    settings: { ...values.settings },
    sections: values.sections.map(({ key, enabled, options }) => ({ key, enabled, options: options || {} })),
    options: { ...values.options, prepared_by: values.options.prepared_by || null },
});

/**
 * A saved configuration is reused across periods, so it carries no period,
 * as-at date, or movement basis — the basis is a specific issued pack from
 * *this* period's register, and the default (the most recent issued pack) is
 * only correct if it is re-resolved when the set is loaded next period.
 */
export const savedSetParametersOf = (values) => {
    const configuration = configurationOf(values);

    return {
        ...configuration,
        settings: { ...configuration.settings, period: null, as_at: null, movement_basis_pack_id: null },
    };
};

/**
 * Apply a saved configuration to the form, keeping the selected period, as-at date and scope.
 */
export const applySavedParameters = (values, parameters) => {
    const configuration = normaliseConfiguration(parameters);

    return {
        ...values,
        ...configuration,
        settings: { ...configuration.settings, period: values.settings.period, as_at: values.settings.as_at },
    };
};

export const packPayload = (values) => ({
    period: values.settings.period,
    as_at: values.settings.as_at,
    costcentre_ids: values.scope.org ? null : sortedIds(values.scope.costcentreIds),
    configuration: configurationOf(values),
});

/**
 * The configuration as compared for unsaved changes. The server returns a
 * section's empty options as `[]` where the form holds `{}`.
 */
const comparableConfiguration = (configuration) => ({
    ...configuration,
    sections: configuration.sections.map((section) => ({
        ...section,
        options: Array.isArray(section.options) && section.options.length === 0 ? {} : section.options,
    })),
});

/**
 * Whether the form holds the pack's stored configuration, so there is nothing
 * to save. Any difference counts as unsaved.
 */
export const matchesStoredConfiguration = (values, pack) => {
    const stored = normaliseConfiguration(pack?.configuration);

    return isEqual(
        comparableConfiguration(configurationOf(values)),
        comparableConfiguration(configurationOf({ ...stored, settings: { ...stored.settings, period: pack?.period, as_at: pack?.asAt } })),
    );
};

/**
 * An issued pack previews its stored configuration: the form cannot change it.
 */
export const previewPayload = (values, pack) => {
    if (pack?.isIssued) {
        return {
            pack_id: pack.id,
            period: pack.period,
            as_at: pack.asAt,
            costcentre_ids: pack.isOrgScope ? null : pack.costcentreIds,
            configuration: configurationOf(normaliseConfiguration(pack.configuration)),
        };
    }

    return { ...packPayload(values), pack_id: pack?.id ?? null };
};

export const initialValuesFor = (options) => {
    const pack = options.initialPack;
    const periods = options.periods || [];
    const configuration = normaliseConfiguration(pack?.configuration || options.initialConfiguration || {});
    const period = pack?.period ?? periods[0]?.value ?? null;
    const defaultAsAt = periods.find((option) => option.value === period)?.defaultAsAt ?? null;

    let scope;
    if (pack) {
        scope = { org: !!pack.isOrgScope, costcentreIds: pack.isOrgScope ? [] : sortedIds(pack.costcentreIds) };
    } else if (options.canUseOrgScope) {
        scope = { org: true, costcentreIds: [] };
    } else {
        scope = { org: false, costcentreIds: (options.costcentres || []).map((costcentre) => costcentre.value) };
    }

    return {
        ...configuration,
        settings: {
            ...configuration.settings,
            period,
            as_at: pack?.asAt ?? defaultAsAt,
            comparison_budget_id: configuration.settings.comparison_budget_id ?? options.defaultComparisonBudgetId ?? null,
        },
        scope,
    };
};

const today = () => {
    const now = new Date();

    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

export const validationSchema = () =>
    Yup.object({
        settings: Yup.object({
            period: Yup.string().nullable().required($t('Select a reporting period.')),
            as_at: Yup.string()
                .nullable()
                .required($t('Select an as-at date.'))
                .test('in-period', $t('The as-at date must fall inside the reporting period.'), function (value) {
                    const period = this.parent.period;

                    return !value || !period || value.slice(0, 7) === period.slice(0, 7);
                })
                .test('not-future', $t('The as-at date cannot be in the future.'), (value) => !value || value <= today()),
            confidentiality_label: Yup.string().trim().required($t('Enter a confidentiality label.')).max(100),
        }),
        options: Yup.object({
            prepared_by: Yup.string().nullable().max(255),
        }),
        sections: Yup.array().test(
            'workforce-types',
            $t('Select at least one workforce type.'),
            (sections) =>
                !(sections || []).some(
                    (section) => section.key === 'workforce' && section.enabled && Array.isArray(section.options?.types) && !section.options.types.length,
                ),
        ),
        scope: Yup.object({
            org: Yup.boolean(),
            costcentreIds: Yup.array().when('org', {
                is: false,
                then: (schema) => schema.min(1, $t('Select at least one cost centre.')),
            }),
        }),
    });

/**
 * A readable message from a failed request: the server's own message for the
 * refusals the pack endpoints raise deliberately, otherwise the fallback.
 */
export const errorMessage = (error, fallback) => {
    const status = error?.response?.status;
    const data = error?.response?.data;

    if (status === 422) {
        const first = data?.errors ? Object.values(data.errors)[0] : null;

        return (Array.isArray(first) ? first[0] : first) || data?.message || fallback;
    }

    if (status === 409 && data?.error_code === 'MANAGEMENT_PACK_EXPORT_IN_PROGRESS') {
        return $t('An export for this pack is still running. Try again when it has finished.');
    }

    if (status === 409 && data?.error_code === 'MANAGEMENT_PACK_REISSUE_REFUSED') {
        return $t('Only the latest issued revision can be reissued, and only while no revision is open. Reload the page to see the latest revision.');
    }

    if ((status === 403 || status === 409) && data?.message) {
        return data.message;
    }

    return fallback;
};
